import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, MagnifyingGlass, TrendUp, TrendDown, Coins, Calendar, User, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCurrency } from '../context/CurrencyContext';

const DURATION_PRESETS = [
  { label: '1h',  hours: 1 },
  { label: '24h', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '1 week', hours: 168 },
  { label: '1 month', hours: 720 },
];

const buildDeadlineISO = (hoursFromNow) => {
  const dt = new Date(Date.now() + hoursFromNow * 3600 * 1000);
  return dt.toISOString();
};

const CreateBetModal = ({ onClose, onCreated }) => {
  const { format: fmt, displayCcy, convert } = useCurrency();
  const [step, setStep] = useState(0); // 0 = pick stock, 1 = prediction, 2 = invite
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [portfolio, setPortfolio] = useState([]);
  const [selected, setSelected] = useState(null); // {symbol, name, price, price_usd}
  const [direction, setDirection] = useState('above');
  const [targetPrice, setTargetPrice] = useState(''); // entered in display currency
  const [durationHours, setDurationHours] = useState(168);
  const [opponentUsername, setOpponentUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const searchTimer = useRef(null);

  // Load portfolio holdings for quick-pick
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/portfolio');
        if (cancelled) return;
        setPortfolio((res.data?.portfolio || []).slice(0, 8));
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchQuote = async (symbol) => {
    try {
      const res = await api.get(`/stocks/${symbol}`);
      return res.data;
    } catch { return null; }
  };

  // Search debounced
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!search.trim() || search.length < 1) {
      setResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/stocks/search?query=${encodeURIComponent(search.trim())}&limit=10`);
        setResults(res.data?.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
  }, [search]);

  const choose = async (item) => {
    const q = await fetchQuote(item.symbol);
    const merged = {
      symbol: item.symbol,
      name: item.name || item.company_name || item.symbol,
      price_usd: q?.price_usd || q?.price || 0,
      price_native: q?.price || 0,
      native_currency: q?.native_currency || 'USD',
    };
    setSelected(merged);
    // Pre-seed target to +5% above current
    const seed = +(merged.price_usd * 1.05).toFixed(2);
    setTargetPrice(convert(seed).toFixed(2));
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!selected) { toast.error('Pick a stock first'); return; }
    if (!targetPrice || parseFloat(targetPrice) <= 0) { toast.error('Enter a target price'); return; }
    if (!opponentUsername.trim()) { toast.error('Who are you betting against?'); return; }

    // Convert target price from displayed currency BACK to USD for storage
    // displayed = USD × rate → USD = displayed / rate
    // Easiest: re-use convert(1) to find the rate, then divide.
    const oneUsdInDisplay = convert(1) || 1;
    const targetUsd = parseFloat(targetPrice) / oneUsdInDisplay;

    setSubmitting(true);
    try {
      await api.post('/bets', {
        symbol: selected.symbol,
        direction,
        target_price: targetUsd,
        deadline: buildDeadlineISO(durationHours),
        opponent_username: opponentUsername.trim(),
      });
      toast.success(`Bet sent to @${opponentUsername.trim()} 🚀`);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create bet');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="create-bet-modal">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="w-full max-w-lg bg-zinc-950 border border-white/10 rounded-3xl shadow-2xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
              <Coins size={22} weight="fill" className="text-black" />
            </div>
            <div>
              <h2 className="text-xl font-unbounded font-bold text-white">Create a bet</h2>
              <p className="text-xs text-zinc-400">
                {step === 0 ? 'Step 1 · Pick a stock' : step === 1 ? 'Step 2 · Make your call' : 'Step 3 · Invite a friend'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="create-bet-close">
            <X size={20} />
          </button>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5 mb-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${step >= i ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 'bg-white/10'}`} />
          ))}
        </div>

        {step === 0 && (
          <div>
            {portfolio.length > 0 && (
              <>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">From your portfolio</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {portfolio.map((h) => (
                    <button
                      key={h.symbol}
                      onClick={() => choose({ symbol: h.symbol, name: h.company_name || h.symbol })}
                      className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/40 text-sm text-zinc-200 transition-all"
                      data-testid={`bet-portfolio-${h.symbol}`}
                    >
                      <span className="font-bold text-white">{h.symbol}</span>
                      <span className="text-zinc-500 ml-1.5 text-xs">{h.shares.toFixed(2)}sh</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Or search any stock</p>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-3.5 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="AAPL, TSLA, RELIANCE.NS..."
                className="bg-black/40 border-white/10 pl-10"
                data-testid="bet-stock-search"
              />
            </div>
            <div className="mt-3 space-y-1 max-h-56 overflow-y-auto">
              {searching && <p className="text-zinc-500 text-xs">Searching...</p>}
              {!searching && results.map((r) => (
                <button
                  key={r.symbol}
                  onClick={() => choose(r)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                  data-testid={`bet-search-result-${r.symbol}`}
                >
                  <span>
                    <span className="font-bold text-white">{r.symbol}</span>
                    <span className="text-zinc-400 text-sm ml-2">{r.name || r.company_name}</span>
                  </span>
                  <span className="text-xs text-zinc-500">{r.asset_type || ''}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && selected && (
          <div className="space-y-5">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between">
              <div>
                <p className="font-unbounded font-bold text-white text-lg">{selected.symbol}</p>
                <p className="text-xs text-zinc-400 truncate max-w-[200px]">{selected.name}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase text-zinc-500">Current</p>
                <p className="font-mono font-bold text-white">{fmt(selected.price_usd)}</p>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Your prediction</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { code: 'above', label: 'Will go ABOVE', icon: TrendUp, gradient: 'from-emerald-400 to-emerald-600', textCls: 'text-emerald-300' },
                  { code: 'below', label: 'Will stay BELOW', icon: TrendDown, gradient: 'from-pink-400 to-pink-600', textCls: 'text-pink-300' },
                ].map((d) => {
                  const Icon = d.icon;
                  const active = direction === d.code;
                  return (
                    <button
                      key={d.code}
                      onClick={() => setDirection(d.code)}
                      className={`p-3 rounded-xl border-2 transition-all ${active ? `border-transparent bg-gradient-to-br ${d.gradient} text-black font-bold` : `border-white/10 bg-white/5 ${d.textCls} hover:bg-white/10`}`}
                      data-testid={`bet-direction-${d.code}`}
                    >
                      <Icon size={20} weight="fill" className="mx-auto mb-1" />
                      <p className="text-xs font-bold uppercase">{d.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Target price ({displayCcy})</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                className="bg-black/40 border-white/10 font-mono text-lg"
                data-testid="bet-target-price"
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                Current: {fmt(selected.price_usd)} · You bet it will {direction === 'above' ? 'rise to or past' : 'stay under'} this number by deadline.
              </p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Calendar size={12} /> Deadline</p>
              <div className="flex flex-wrap gap-2">
                {DURATION_PRESETS.map((p) => {
                  const active = durationHours === p.hours;
                  return (
                    <button
                      key={p.hours}
                      onClick={() => setDurationHours(p.hours)}
                      className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition-all ${active ? 'bg-gold/20 text-gold border-gold/40' : 'bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10'}`}
                      data-testid={`bet-duration-${p.hours}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setStep(0)} className="flex-1 border border-white/10" data-testid="bet-back-to-stock">← Back</Button>
              <Button onClick={() => setStep(2)} className="flex-[2] bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full" data-testid="bet-next-to-invite">
                Next →
              </Button>
            </div>
          </div>
        )}

        {step === 2 && selected && (
          <div className="space-y-5">
            <div className="p-4 rounded-xl bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border border-yellow-400/20">
              <p className="text-sm text-zinc-300">
                <span className="text-white font-semibold">I bet {selected.symbol}</span> will go{' '}
                <span className={`font-bold ${direction === 'above' ? 'text-emerald-300' : 'text-pink-300'}`}>{direction === 'above' ? 'above' : 'below'}</span>{' '}
                <span className="font-mono font-bold text-gold">
                  {targetPrice ? `${displayCcy === 'USD' ? '$' : ''}${parseFloat(targetPrice).toLocaleString()}` : '—'}
                </span>{' '}
                in <span className="font-semibold text-white">{DURATION_PRESETS.find(p => p.hours === durationHours)?.label}</span>.
              </p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><User size={12} /> Friend&apos;s TeenVest username</label>
              <Input
                value={opponentUsername}
                onChange={(e) => setOpponentUsername(e.target.value)}
                placeholder="e.g. bettest2"
                className="bg-black/40 border-white/10"
                data-testid="bet-opponent-username"
              />
              <p className="text-[10px] text-zinc-500 mt-1">They must already have a TeenVest account.</p>
            </div>

            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setStep(1)} className="flex-1 border border-white/10" data-testid="bet-back-to-prediction">← Back</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !opponentUsername.trim() || !targetPrice}
                className="flex-[2] bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full"
                data-testid="bet-submit-button"
              >
                {submitting ? 'Sending invite...' : <>Send Bet Invite <Check size={16} weight="bold" className="ml-1.5" /></>}
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CreateBetModal;
